/**
 * Reine Logik des Kalenders (Agentur und Creator): Einträge aus calendar_feed, eigenen Terminen, Überschreibungen und
 * Feiertagen zusammensetzen, Monatsraster, Filter, Beschriftungen, Zeitzonen, Formulare. Ohne Datenbank, mit Tests.
 *
 * Quellen: 0015_kalender.sql / 0016_kooperationen.sql (calendar_feed), bisheriges Portal common/calendar.js
 * (AD-187 … AD-192, TA-120 … TA-126).
 */
import { addDays, addMonths, deliverableLabel, diffDays, holidayName, isIsoDate, lastDayOfMonth, platformName, publicHolidays, weekday } from "@talentkontor/domain";
import type { Pill, Tone } from "../../../model/invoice-view";
import type { Lang } from "../../creator/lang";
import { zonenAuswahl } from "../zonen";
import { currentLang, t, ta, istCreatorKonto } from "../../../lib/sprache";

export type Perspektive = "agentur" | "creator";
export type AutoArt = "invoice_due" | "payout_due" | "period_requested" | "content_deadline" | "usage_rights_end" | "exclusive_end"
  | "deliverable_draft" | "deliverable_publish";
export type EintragArt = "event" | AutoArt | "holiday";
export type Farbe = "grey" | "red" | "blue" | "yellow" | "green" | "orange" | "purple" | "lightblue" | "pink";
export type Zielgruppe = "staff" | "all" | "group" | "talent";

// ── Zeilen aus der Datenbank ───────────────────────────────────────────────

/** Zeile von calendar_feed(p_from, p_to): eigene und automatische Termine, Überschreibungen schon angewandt. */
export interface FeedRow {
  id: string;
  kind: string;
  title: string;
  starts_on: string;
  ends_on: string | null;
  start_time: string | null;
  end_time: string | null;
  time_zone: string | null;
  color: string;
  talent_id: string | null;
  link_kind: string | null;
  link_id: string | null;
  editable: boolean;
  /** Agentur des Eintrags (0054); der Feed liefert die Termine aller Agenturen der Person */
  org_id?: string | null;
}

/** Zeile aus calendar_events (eigener Termin oder Überschreibung eines automatischen Termins über source_key). */
export interface TerminRow {
  id: string;
  org_id: string;
  title: string;
  description: string | null;
  starts_on: string;
  ends_on: string | null;
  start_time: string | null;
  end_time: string | null;
  time_zone: string;
  /** Ort (0041), in Mail und Detail */
  location?: string | null;
  /** 0395 (K-527): Link für Video-Calls, im Detail, in Glocke/Mail und im Kalender-Abo */
  meeting_url?: string | null;
  /** 0395 (K-527): ein Termin für mehrere Creator (je Creator eine Zeile, gleiche Gruppe) */
  copy_group?: string | null;
  color: string;
  audience: Zielgruppe;
  group_id: string | null;
  talent_id: string | null;
  source_key: string | null;
  hidden: boolean;
  created_by: string | null;
  created_by_side: "agency" | "talent";
  created_at: string;
  updated_at: string;
}

export interface TalentLite {
  id: string;
  first_name: string;
  last_name: string | null;
  display_name: string | null;
  nickname?: string | null;
  status: string;
}
export interface GruppeRow {
  id: string;
  name: string;
}
export interface MitgliedRow {
  group_id: string;
  talent_id: string;
}
export interface DealLite {
  id: string;
  title: string;
  brand_id: string;
  talent_id: string;
  status: string;
}
export interface BrandLite {
  id: string;
  name: string;
}
export interface InvoiceLite {
  id: string;
  number: string | null;
  kind: string;
  brand_id: string | null;
  deal_id: string | null;
  talent_id: string | null;
  gross_cents: number | null;
  currency: string;
}
export interface PeriodLite {
  id: string;
  deal_id: string;
  month: string;
}
export interface MitarbeiterLite {
  user_id: string;
  display_name: string | null;
}
/** Beitrag einer Kooperation (0068) für Entwurfsfrist und Veröffentlichung: Agentur deal_deliverables, Creator talent_deal_deliverables */
export interface BeitragLite {
  id: string;
  deal_id: string;
  period_id: string | null;
  platform: string;
  format: string;
  format_label: string | null;
  title: string | null;
  status: string;
}

// ── Ein Eintrag im Kalender ───────────────────────────────────────────────

export interface Eintrag {
  /** Kennung aus dem Feed (Termin-ID oder `art:uuid`), bei Feiertagen `holiday:<datum>` */
  key: string;
  art: EintragArt;
  titel: string;
  beginn: string;
  ende: string | null;
  von: string | null;
  bis: string | null;
  zeitzone: string | null;
  farbe: Farbe;
  talentId: string | null;
  linkKind: string | null;
  linkId: string | null;
  bearbeitbar: boolean;
  /** Eigener Termin (art = event) */
  termin?: TerminRow;
  /** Überschreibung eines automatischen Termins (Titel, Datum, Farbe, Ausblenden) */
  ueberschreibung?: TerminRow;
  /** Ausgeblendeter automatischer Termin (nur mit „Ausgeblendete zeigen“) */
  ausgeblendet?: boolean;
  /** Deadline der ganzen Kooperation statt eines Monats */
  kooperationsweit?: boolean;
  /** Mitgeführte Einträge am selben Tag (Deadline der Kooperation hinter der Monats-Deadline); Aktionen gelten für alle */
  mit?: Eintrag[];
}

export const FARBEN: readonly { key: Farbe; label: string; wert: string }[] = [
  { key: "blue", get label() { return t("Blau", "Blue"); }, wert: "var(--info)" },
  { key: "lightblue", get label() { return t("Hellblau", "Light blue"); }, wert: "color-mix(in srgb, var(--info) 45%, var(--surface))" },
  { key: "green", get label() { return t("Grün", "Green"); }, wert: "var(--success)" },
  { key: "yellow", get label() { return t("Gelb", "Yellow"); }, wert: "var(--warning)" },
  { key: "orange", label: "Orange", wert: "var(--tk-s5)" },
  { key: "red", get label() { return t("Rot", "Red"); }, wert: "var(--error)" },
  { key: "pink", get label() { return t("Rosa", "Pink"); }, wert: "color-mix(in srgb, var(--tk-s6) 55%, var(--surface))" },
  { key: "purple", get label() { return t("Lila", "Purple"); }, wert: "var(--tk-s6)" },
  { key: "grey", get label() { return t("Grau", "Grey"); }, wert: "var(--control)" },
];
const FARB_KEYS = new Set<string>(FARBEN.map((f) => f.key));
export function farbe(v: string | null | undefined): Farbe {
  return v && FARB_KEYS.has(v) ? (v as Farbe) : "blue";
}
export function farbWert(f: string): string {
  return FARBEN.find((x) => x.key === f)?.wert ?? "var(--info)";
}

const ART_LABEL_EN: Record<EintragArt, string> = {
  event: "Date", invoice_due: "Invoice due", payout_due: "Payout due", period_requested: "Invoice requested", content_deadline: "Content deadline",
  usage_rights_end: "Usage rights end", exclusive_end: "Exclusivity ends", holiday: "Public holiday",
  deliverable_draft: "Draft due", deliverable_publish: "Publication",
};
export function artLabel(art: EintragArt, lang: Lang = currentLang()): string {
  return lang === "en" ? ART_LABEL_EN[art] : ART_LABEL[art];
}

export const ART_LABEL: Record<EintragArt, string> = {
  event: "Termin",
  get invoice_due() { return t("Rechnung fällig", "Invoice due"); },
  get payout_due() { return t("Auszahlung fällig", "Payout due"); },
  // steht seit 0047 an der Frist der Creator-Rechnung (Anforderung plus eingestellte Tage)
  get period_requested() { return t("Abrechnung angefordert", "Invoice requested"); },
  get content_deadline() { return t("Content-Deadline", "Content deadline"); },
  get usage_rights_end() { return t("Nutzungsrechte enden", "Usage rights end"); },
  get exclusive_end() { return t("Exklusivität endet", "Exclusivity ends"); },
  get holiday() { return t("Feiertag", "Public holiday"); },
  // 0068: Beiträge
  get deliverable_draft() { return t("Entwurf fällig", "Draft due"); },
  get deliverable_publish() { return t("Veröffentlichung", "publication"); },
};

/** Filterknöpfe nach Art (AD-187). */
export const ART_FILTER: readonly { key: "alle" | "termine" | "content" | "abrechnung" | "zahlung" | "nutzungsrechte" | "feiertage"; label: string; label_en: string; arten: EintragArt[] }[] = [
  { key: "alle", label: "Alle", label_en: "All", arten: [] },
  { key: "termine", label: "Termine", label_en: "Dates", arten: ["event"] },
  { key: "content", label: "Content", label_en: "Content", arten: ["content_deadline", "deliverable_draft", "deliverable_publish"] },
  { key: "abrechnung", label: "Abrechnung", label_en: "Invoicing", arten: ["period_requested"] },
  { key: "zahlung", label: "Zahlungen", label_en: "Payments", arten: ["invoice_due", "payout_due"] },
  { key: "nutzungsrechte", label: "Nutzungsrechte und Exklusivität", label_en: "Usage rights and exclusivity", arten: ["usage_rights_end", "exclusive_end"] },
  { key: "feiertage", label: "Feiertage", label_en: "Holidays", arten: ["holiday"] },
];
export type ArtFilter = (typeof ART_FILTER)[number]["key"];

const AUTO_ARTEN = new Set<string>(["invoice_due", "payout_due", "period_requested", "content_deadline", "usage_rights_end", "exclusive_end",
  "deliverable_draft", "deliverable_publish"]);
const BEITRAG_ARTEN = new Set<string>(["deliverable_draft", "deliverable_publish"]);
export const istAuto = (e: Pick<Eintrag, "art">): boolean => AUTO_ARTEN.has(e.art);

// ── Datum und Zeit ─────────────────────────────────────────────────────────

export const WOCHENTAGE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
export const WOCHENTAGE_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const WOCHENTAG_LANG = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
const WEEKDAY_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONATE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** 'HH:MM:SS' oder 'HH:MM' → 'HH:MM'; leer → null */
export function hm(t: string | null | undefined): string | null {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  return m ? `${m[1]!.padStart(2, "0")}:${m[2]}` : null;
}

/** 24.09. bzw. 24/09 */
export function tagKurz(d: string, lang: Lang = currentLang()): string {
  return lang === "en" ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : `${d.slice(8, 10)}.${d.slice(5, 7)}.`;
}
/** Mittwoch, 24. September 2026 bzw. Wednesday, 24 September 2026 */
export function tagLang(d: string, lang: Lang = currentLang()): string {
  if (lang === "en") return `${WEEKDAY_EN[weekday(d)]}, ${Number(d.slice(8, 10))} ${MONTHS_EN[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
  return `${WOCHENTAG_LANG[weekday(d)]}, ${Number(d.slice(8, 10))}. ${MONATE[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
}
/** 24. September 2026 */
export function tagMittel(d: string, lang: Lang = currentLang()): string {
  if (lang === "en") return `${Number(d.slice(8, 10))} ${MONTHS_EN[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
  return `${Number(d.slice(8, 10))}. ${MONATE[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
}
export function monatTitel(month: string, lang: Lang = currentLang()): string {
  return `${(lang === "en" ? MONTHS_EN : MONATE)[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
}

/** Monatsraster Montag bis Sonntag wie in der Vorlage: leere Felder vor dem Ersten, dann alle Tage des Monats. */
export function monatsRaster(month: string): { leer: number; tage: string[] } {
  const first = `${month}-01`;
  const last = lastDayOfMonth(month);
  const leer = (weekday(first) + 6) % 7;
  const tage: string[] = [];
  for (let d = first; d <= last; d = addDays(d, 1)) tage.push(d);
  return { leer, tage };
}

/** Zeitversatz einer Zone zu UTC in Minuten zu einem Zeitpunkt; null bei unbekannter Zone. */
function versatzMinuten(zone: string, instant: Date): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(instant);
    const p: Record<string, number> = {};
    for (const x of parts) if (x.type !== "literal") p[x.type] = Number(x.value);
    const asUtc = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour! % 24, p.minute!, p.second!);
    return Math.round((asUtc - instant.getTime()) / 60000);
  } catch {
    return null;
  }
}

/**
 * Uhrzeit eines Termins aus seiner Zone in die Zone der betrachtenden Person umrechnen (TA-124).
 * null, wenn beide Zonen gleich sind oder eine Zone unbekannt ist (dann gilt die Uhrzeit wie eingegeben).
 */
export function zeitUmrechnen(datum: string, zeit: string, vonZone: string | null, nachZone: string | null): { zeit: string; datum: string; versatz: number } | null {
  const t = hm(zeit);
  if (!t || !vonZone || !nachZone || vonZone === nachZone) return null;
  const [y, mo, d] = datum.split("-").map(Number) as [number, number, number];
  const [h, mi] = t.split(":").map(Number) as [number, number];
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const off = versatzMinuten(vonZone, new Date(guess));
  if (off === null) return null;
  let instant = new Date(guess - off * 60000);
  const off2 = versatzMinuten(vonZone, instant);
  if (off2 !== null && off2 !== off) instant = new Date(guess - off2 * 60000);
  const ziel = versatzMinuten(nachZone, instant);
  if (ziel === null) return null;
  const local = new Date(instant.getTime() + ziel * 60000);
  const outDatum = local.toISOString().slice(0, 10);
  const outZeit = local.toISOString().slice(11, 16);
  if (outZeit === t && outDatum === datum) return null;
  return { zeit: outZeit, datum: outDatum, versatz: diffDays(datum, outDatum) };
}

/** Beginn in der Zone der betrachtenden Person („10:00“); fällt er dort auf einen anderen Tag, mit Versatz („01:00 (+1)“). */
export function startZeit(e: Pick<Eintrag, "beginn" | "von" | "zeitzone">, zone: string | null): string {
  if (!e.von) return "";
  const a = zeitUmrechnen(e.beginn, e.von, e.zeitzone, zone);
  if (!a) return e.von;
  return a.versatz ? `${a.zeit} (${a.versatz > 0 ? "+" : "−"}${Math.abs(a.versatz)})` : a.zeit;
}

/** Anzeige der Uhrzeit: „10:00–10:45“ in der Zone der betrachtenden Person, sonst „ganztägig“. */
export function zeitText(e: Pick<Eintrag, "beginn" | "von" | "bis" | "zeitzone">, zone: string | null, lang: Lang = currentLang()): { text: string; hinweis: string | null } {
  if (!e.von) return { text: lang === "en" ? "all day" : "ganztägig", hinweis: null };
  const a = zeitUmrechnen(e.beginn, e.von, e.zeitzone, zone);
  const b = e.bis ? zeitUmrechnen(e.beginn, e.bis, e.zeitzone, zone) : null;
  const original = `${e.von}${e.bis ? `–${e.bis}` : ""}`;
  if (!a) return { text: original, hinweis: null };
  const tag = a.versatz ? ` (${a.versatz > 0 ? "+" : "−"}${Math.abs(a.versatz)} ${lang === "en" ? "day" : "Tag"})` : "";
  return {
    text: `${a.zeit}${b ? `–${b.zeit}` : e.bis ? `–${e.bis}` : ""}${tag}`,
    hinweis: lang === "en" ? `your time (${zone}) · original ${original} ${e.zeitzone}` : `deine Zeit (${zone}) · Original ${original} ${e.zeitzone}`,
  };
}

/**
 * Tage, an denen ein Eintrag für die betrachtende Person liegt (Befund 15): Termine mit Uhrzeit in einer anderen Zone
 * rutschen auf den Tag in ihrer Zone („Call Dubai“ 01:00 steht in Berlin am Vortag). Ganztägige Einträge bleiben.
 */
export function lokaleTage(e: Pick<Eintrag, "beginn" | "ende" | "von" | "zeitzone">, zone: string | null | undefined): { beginn: string; ende: string | null } {
  if (!e.von || !zone || !e.zeitzone || zone === e.zeitzone) return { beginn: e.beginn, ende: e.ende };
  const a = zeitUmrechnen(e.beginn, e.von, e.zeitzone, zone);
  if (!a || !a.versatz) return { beginn: e.beginn, ende: e.ende };
  return { beginn: addDays(e.beginn, a.versatz), ende: e.ende ? addDays(e.ende, a.versatz) : null };
}

// ── Feiertage (AD-191; Befund 13: alle Länder, Österreich, alle Kantone, Namen je Sprache aus der Fachlogik) ─────

export function feiertagName(key: string, region: string, lang: Lang = currentLang()): string {
  return holidayName(key, region, lang);
}

/** Gesetzliche Feiertage der Region als schreibgeschützte Einträge; unbekannte Region ergibt keine. */
export function feiertage(region: string | null, jahre: readonly number[], lang: Lang = currentLang()): Eintrag[] {
  if (!region) return [];
  const out: Eintrag[] = [];
  for (const y of jahre) {
    let list: { date: string; key: string }[];
    try {
      list = publicHolidays(region, y);
    } catch {
      return [];
    }
    for (const h of list) {
      out.push({
        key: `holiday:${h.date}`, art: "holiday", titel: feiertagName(h.key, region, lang), beginn: h.date, ende: null, von: null, bis: null, zeitzone: null,
        farbe: "grey", talentId: null, linkKind: null, linkId: null, bearbeitbar: false,
      });
    }
  }
  return out;
}

// ── Einträge zusammensetzen ────────────────────────────────────────────────

export interface EintragQuelle {
  feed: readonly FeedRow[];
  termine: readonly TerminRow[];
  perspektive: Perspektive;
  /** Talent-Datensätze der gewählten Agentur (der Feed liefert alle Agenturen der Person) */
  talentIds: ReadonlySet<string>;
  /** Rechnungen, Kooperationen und Monate der gewählten Agentur */
  linkIds: ReadonlySet<string>;
  /** Creator: eigener Talent-Datensatz in dieser Agentur */
  eigeneTalentId?: string | null;
  region: string | null;
  jahre: readonly number[];
  /** Sprache der Feiertagsnamen (Creator-Portal auf Englisch, Befund 9) */
  lang?: Lang;
  /** Beiträge (0068): ausgeblendete Einträge zeigen auf ihre Kooperation */
  beitraege?: readonly Pick<BeitragLite, "id" | "deal_id">[];
}

/**
 * Feed, eigene Termine, Überschreibungen und Feiertage zu einer Liste. Der Feed kennt keine Agentur: Termine gelten, wenn
 * ihre Zeile zur gewählten Agentur gehört; automatische Termine, wenn Talent oder verknüpftes Objekt zur Agentur gehört.
 * Ausgeblendete automatische Termine liefert der Feed nicht; die Agentur bekommt sie aus ihren Überschreibungen.
 */
export function baueEintraege(q: EintragQuelle): Eintrag[] {
  const termine = new Map(q.termine.filter((t) => !t.source_key).map((t) => [t.id, t]));
  const ueber = new Map(q.termine.filter((t) => t.source_key).map((t) => [t.source_key!, t]));
  const out: Eintrag[] = [];
  const seen = new Set<string>();
  // K-527: ein Termin für mehrere Creator steht im Kalender der Agentur einmal (je Creator gibt es eine Zeile)
  const gruppen = new Set<string>();
  for (const f of q.feed) {
    if (seen.has(f.id)) continue;
    const art = f.kind as EintragArt;
    if (art === "event") {
      const t = termine.get(f.id);
      if (!t) continue;
      seen.add(f.id);
      if (q.perspektive === "agentur" && t.copy_group) {
        if (gruppen.has(t.copy_group)) continue;
        gruppen.add(t.copy_group);
      }
      out.push({
        key: f.id, art, titel: f.title, beginn: f.starts_on, ende: f.ends_on && f.ends_on > f.starts_on ? f.ends_on : null,
        von: hm(f.start_time), bis: hm(f.end_time), zeitzone: f.time_zone, farbe: farbe(f.color), talentId: f.talent_id,
        linkKind: null, linkId: null, bearbeitbar: f.editable, termin: t,
      });
      continue;
    }
    if (!AUTO_ARTEN.has(art)) continue;
    const eigen = q.perspektive === "creator"
      ? !!f.talent_id && f.talent_id === q.eigeneTalentId
      : (!!f.talent_id && q.talentIds.has(f.talent_id)) || (!!f.link_id && q.linkIds.has(f.link_id));
    if (!eigen) continue;
    seen.add(f.id);
    out.push({
      key: f.id, art, titel: f.title, beginn: f.starts_on, ende: null, von: null, bis: null, zeitzone: f.time_zone, farbe: farbe(f.color),
      talentId: f.talent_id, linkKind: f.link_kind, linkId: f.link_id, bearbeitbar: q.perspektive === "agentur" && f.editable,
      kooperationsweit: art === "content_deadline" && f.link_kind === "deal", ueberschreibung: ueber.get(f.id),
    });
  }
  if (q.perspektive === "agentur") {
    for (const t of q.termine) {
      if (!t.source_key || !t.hidden || seen.has(t.source_key)) continue;
      const art = t.source_key.split(":")[0] === "period_deadline" ? "content_deadline" : (t.source_key.split(":")[0] as EintragArt);
      if (!AUTO_ARTEN.has(art)) continue;
      const beitrag = BEITRAG_ARTEN.has(art) ? q.beitraege?.find((b) => b.id === t.source_key!.split(":")[1]) : undefined;
      if (BEITRAG_ARTEN.has(art) && !beitrag) continue;
      const linkId = beitrag ? beitrag.deal_id : t.source_key.split(":")[1] ?? null;
      const linkKind = art === "invoice_due" ? "invoice" : t.source_key.startsWith("period") ? "period" : "deal";
      out.push({
        key: t.source_key, art, titel: t.title, beginn: t.starts_on, ende: null, von: null, bis: null, zeitzone: t.time_zone, farbe: farbe(t.color),
        talentId: t.talent_id, linkKind, linkId, bearbeitbar: true, ueberschreibung: t, ausgeblendet: true,
        kooperationsweit: art === "content_deadline" && linkKind === "deal",
      });
    }
  }
  out.push(...feiertage(q.region, q.jahre, q.lang ?? currentLang()));
  return out;
}

/**
 * Deadline der Kooperation und Content-Deadline eines Monats am selben Tag sind dieselbe Frist: im Kalender steht nur die
 * Monats-Deadline, die Kooperations-Deadline wird mitgeführt (Anpassen und Ausblenden wirken auf beide).
 */
export function fasseZusammen(list: readonly Eintrag[], n: Pick<Nachschlag, "kooperation">): Eintrag[] {
  const kopien = list.map((e) => ({ ...e }));
  const schluessel = (dealId: string | null | undefined, e: Eintrag) => `${dealId}|${e.beginn}|${e.ausgeblendet ? 1 : 0}`;
  const monatsFrist = new Map<string, Eintrag>();
  for (const e of kopien) {
    if (e.art !== "content_deadline" || e.kooperationsweit) continue;
    const koop = n.kooperation(e);
    if (koop) monatsFrist.set(schluessel(koop.id, e), e);
  }
  const out: Eintrag[] = [];
  for (const e of kopien) {
    const ziel = e.art === "content_deadline" && e.kooperationsweit ? monatsFrist.get(schluessel(e.linkId, e)) : undefined;
    if (ziel) ziel.mit = [...(ziel.mit ?? []), e];
    else out.push(e);
  }
  return out;
}

/**
 * Einträge, die an einem Tag liegen (mehrtägige an jedem Tag ihres Zeitraums), sortiert: Feiertage, ganztägige, nach Uhrzeit.
 * Mit Zone: Termine mit Uhrzeit am Tag in der Zone der betrachtenden Person (Befund 15).
 */
export function amTag(list: readonly Eintrag[], tag: string, zone?: string | null): Eintrag[] {
  return list.filter((e) => {
    const l = lokaleTage(e, zone);
    return l.beginn <= tag && (l.ende ?? l.beginn) >= tag;
  }).sort(sortiere);
}

const ART_RANG: Record<EintragArt, number> = {
  holiday: 0, invoice_due: 1, payout_due: 1, content_deadline: 2, deliverable_draft: 2, deliverable_publish: 2, period_requested: 3, usage_rights_end: 4,
  exclusive_end: 4, event: 5,
};
export function sortiere(a: Eintrag, b: Eintrag): number {
  return a.beginn.localeCompare(b.beginn) || (a.von ?? "").localeCompare(b.von ?? "") || ART_RANG[a.art] - ART_RANG[b.art] || a.titel.localeCompare(b.titel, "de");
}

export interface Filter {
  /** "" = alle, "team" = nur interne Termine, sonst Talent-ID */
  talentId: string;
  art: ArtFilter;
  ausgeblendete: boolean;
}

/**
 * Filter der Agentur (AD-187): Mit Creator-Auswahl bleiben Feiertage und Termine für alle sichtbar, Gruppentermine nur mit
 * dem Creator als Mitglied, interne Termine fallen weg (wie im bisherigen Portal).
 */
export function filtere(list: readonly Eintrag[], f: Filter, gruppen: ReadonlyMap<string, readonly string[]>): Eintrag[] {
  const arten = ART_FILTER.find((x) => x.key === f.art)?.arten ?? [];
  return list.filter((e) => {
    if (e.ausgeblendet && !f.ausgeblendete) return false;
    if (arten.length && !arten.includes(e.art)) return false;
    if (!f.talentId) return true;
    if (e.art === "holiday") return true;
    const aud = e.termin?.audience;
    if (f.talentId === "team") return e.art === "event" && aud === "staff";
    if (e.art === "event") {
      if (aud === "all") return true;
      if (aud === "group") return (gruppen.get(e.termin?.group_id ?? "") ?? []).includes(f.talentId);
      return e.talentId === f.talentId;
    }
    return e.talentId === f.talentId;
  });
}

// ── Beschriftungen ─────────────────────────────────────────────────────────

const SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
const pill = (label: string, tone: Tone): Pill => ({ label, tone, symbol: SYMBOL[tone] });

/** Nachschlagen von Namen und Verknüpfungen, gebaut aus den geladenen Zeilen. */
export interface Nachschlag {
  talent: (id: string | null | undefined) => TalentLite | undefined;
  marke: (e: Eintrag) => string | null;
  kooperation: (e: Eintrag) => { id: string; titel: string; monat: string | null } | null;
  rechnung: (e: Eintrag) => InvoiceLite | undefined;
  gruppe: (id: string | null | undefined) => { name: string; mitglieder: string[] } | undefined;
  mitarbeiter: (userId: string | null | undefined) => string | null;
  /** Beitrag eines Eintrags „Entwurf fällig“ oder „Veröffentlichung“ (0068) */
  beitrag: (e: Eintrag) => BeitragLite | undefined;
  /** K-527: Creator aller Zeilen eines Termins für mehrere Creator (copy_group) */
  gruppenTalente?: (copyGroup: string) => string[];
}

export function nachschlag(d: {
  talents: readonly TalentLite[]; gruppen: readonly GruppeRow[]; mitglieder: readonly MitgliedRow[]; deals: readonly DealLite[];
  brands: readonly BrandLite[]; invoices: readonly InvoiceLite[]; perioden: readonly PeriodLite[]; mitarbeiter: readonly MitarbeiterLite[];
  beitraege?: readonly BeitragLite[];
  termine?: readonly TerminRow[];
}): Nachschlag {
  const beitraege = new Map((d.beitraege ?? []).map((b) => [b.id, b]));
  const talents = new Map(d.talents.map((t) => [t.id, t]));
  const deals = new Map(d.deals.map((x) => [x.id, x]));
  const brands = new Map(d.brands.map((b) => [b.id, b.name]));
  const invoices = new Map(d.invoices.map((i) => [i.id, i]));
  const perioden = new Map(d.perioden.map((p) => [p.id, p]));
  const staff = new Map(d.mitarbeiter.map((m) => [m.user_id, m.display_name]));
  const gruppen = new Map(d.gruppen.map((g) => [g.id, { name: g.name, mitglieder: d.mitglieder.filter((m) => m.group_id === g.id).map((m) => m.talent_id) }]));
  const dealOf = (e: Eintrag) => {
    if (e.linkKind === "deal") return deals.get(e.linkId ?? "");
    if (e.linkKind === "period") return deals.get(perioden.get(e.linkId ?? "")?.deal_id ?? "");
    if (e.linkKind === "invoice") return deals.get(invoices.get(e.linkId ?? "")?.deal_id ?? "");
    return undefined;
  };
  return {
    talent: (id) => (id ? talents.get(id) : undefined),
    marke: (e) => {
      if (e.linkKind === "invoice") {
        const inv = invoices.get(e.linkId ?? "");
        const b = inv?.brand_id ?? (inv?.deal_id ? deals.get(inv.deal_id)?.brand_id : undefined);
        return b ? brands.get(b) ?? null : null;
      }
      const deal = dealOf(e);
      return deal ? brands.get(deal.brand_id) ?? null : null;
    },
    kooperation: (e) => {
      const deal = dealOf(e);
      if (!deal) return null;
      const monat = e.linkKind === "period" ? perioden.get(e.linkId ?? "")?.month.slice(0, 7) ?? null : null;
      return { id: deal.id, titel: deal.title, monat };
    },
    rechnung: (e) => (e.linkKind === "invoice" ? invoices.get(e.linkId ?? "") : undefined),
    gruppe: (id) => (id ? gruppen.get(id) : undefined),
    mitarbeiter: (id) => (id ? staff.get(id) ?? null : null),
    beitrag: (e) => (BEITRAG_ARTEN.has(e.art) ? beitraege.get(e.key.split(":")[1] ?? "") : undefined),
    gruppenTalente: (g) => [...new Set((d.termine ?? []).filter((x) => x.copy_group === g && x.talent_id).map((x) => x.talent_id!))],
  };
}

/** „Reel · Instagram“ zu einem Beitragseintrag (ohne geladenen Beitrag: null). */
export function beitragText(e: Eintrag, n: Pick<Nachschlag, "beitrag">, lang: Lang = currentLang()): string | null {
  const b = n.beitrag(e);
  if (!b) return null;
  // Plattformnamen aus der Fachlogik, gleich wie im Kalender-Abo (packages/server/src/ics.ts, KETTE-15)
  return deliverableLabel(b, (k) => platformName(k, lang), lang);
}

export function talentVoll(t: TalentLite | undefined): string {
  if (!t) return "Creator";
  return t.display_name ?? `${t.first_name} ${t.last_name ?? ""}`.trim();
}
export function talentRuf(t: TalentLite | undefined): string {
  if (!t) return "Creator";
  return t.nickname?.trim() || t.first_name;
}

/**
 * Stand eines Eintrags „Entwurf fällig“ (SICHT-AGENTUR-REST-15): offen (Creator am Zug), eingereicht (das Team prüft)
 * oder Überarbeitung angefordert (wieder der Creator). Ohne geladenen Beitrag gilt er als offen.
 */
export type EntwurfStand = "open" | "submitted" | "revise";
export function entwurfStand(e: Eintrag, n?: Pick<Nachschlag, "beitrag">): EntwurfStand | null {
  if (e.art !== "deliverable_draft") return null;
  const st = n?.beitrag(e)?.status;
  return st === "draft_submitted" ? "submitted" : st === "changes_requested" ? "revise" : "open";
}

/** Überfällig: automatische Frist, deren Tag vor heute liegt. */
export function ueberfaellig(e: Eintrag, heute: string, n?: Pick<Nachschlag, "beitrag">): boolean {
  if (e.art === "deliverable_draft" || e.art === "deliverable_publish") {
    if (e.beginn >= heute) return false;
    // Ohne geladenen Stand zählt der Eintrag als offen (der Feed zeigt die Entwurfsfrist nur bis zur Freigabe)
    const st = n?.beitrag(e)?.status;
    return e.art === "deliverable_draft" ? !st || st === "planned" : st !== "live";
  }
  return (e.art === "invoice_due" || e.art === "payout_due" || e.art === "content_deadline") && e.beginn < heute;
}

/** Rechnung der Agentur an den Creator (Befund 14): zahlen muss der Creator an die Agentur, nicht die Marke. */
export function agenturAnCreator(e: Eintrag, n: Nachschlag): boolean {
  return e.art === "invoice_due" && n.rechnung(e)?.kind === "agency_to_talent";
}

/**
 * Pille im Monatsraster, wie die Vorlage („◷ Content · Mara“, Creator: „◷ Dein Content“). Eigene Termine zeigen Uhrzeit und
 * Titel; ihre Farbe steht als Punkt davor.
 */
export function pilleFuer(e: Eintrag, p: Perspektive, n: Nachschlag, heute: string, zone: string | null, lang: Lang = currentLang()): Pill {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const wer = e.talentId ? talentRuf(n.talent(e.talentId)) : null;
  const spaet = ueberfaellig(e, heute, n);
  const creator = p === "creator";
  switch (e.art) {
    case "holiday":
      return pill(e.titel, "neutral");
    case "invoice_due": {
      if (agenturAnCreator(e, n)) return pill(creator ? t("Deine Zahlung an die Agentur", "Your payment to the agency") : t(`Zahlung von ${wer ?? e.titel}`, `Payment from ${wer ?? e.titel}`), spaet ? "error" : "warning");
      const marke = n.marke(e) ?? e.titel;
      return pill(`${t("Zahlung", "Payment")} · ${marke}`, spaet ? "error" : "warning");
    }
    case "payout_due":
      return pill(creator ? t("Deine Auszahlung", "Your payout") : t(`Auszahlung · ${wer ?? e.titel}`, `Payout · ${wer ?? e.titel}`), spaet ? "error" : "warning");
    case "period_requested":
      return pill(creator ? t("Deine Rechnung", "Your invoice") : t(`Abrechnung · ${wer ?? e.titel}`, `Billing · ${wer ?? e.titel}`), "info");
    case "content_deadline": {
      const was = e.kooperationsweit ? (creator ? t("Deine Deadline", "Your deadline") : "Deadline") : creator ? t("Dein Content", "Your content") : "Content";
      return pill(creator ? was : `${was} · ${wer ?? e.titel}`, spaet ? "error" : "warning");
    }
    case "usage_rights_end":
      return pill(creator ? t("Nutzungsrechte enden", "Usage rights end") : t(`Nutzungsrechte · ${wer ?? e.titel}`, `Usage rights · ${wer ?? e.titel}`), "neutral");
    case "exclusive_end":
      return pill(creator ? t("Exklusivität endet", "Exclusivity ends") : t(`Exklusivität · ${wer ?? e.titel}`, `Exclusivity · ${wer ?? e.titel}`), "neutral");
    case "deliverable_draft": {
      const was = beitragText(e, n, lang) ?? e.titel;
      const stand = entwurfStand(e, n);
      // eingereicht: das Team prüft, der Creator wartet (kein Drängen, keine Frist in Rot)
      if (stand === "submitted") return pill(creator ? `${t("Entwurf eingereicht", "Draft submitted")} · ${was}` : `${t("Entwurf prüfen", "Review draft")} · ${wer ?? e.titel}`, "info");
      const wort = stand === "revise" ? t("Überarbeiten", "Revise") : t("Entwurf", "Draft");
      return pill(creator ? `${wort} · ${was}` : `${wort} · ${wer ?? e.titel}`, spaet ? "error" : "warning");
    }
    case "deliverable_publish": {
      const live = n.beitrag(e)?.status === "live";
      const was = beitragText(e, n, lang) ?? e.titel;
      return pill(creator ? `${live ? "Live" : t("Posten", "Post")} · ${was}` : `${live ? "Live" : "Posting"} · ${wer ?? e.titel}`, live ? "success" : spaet ? "error" : "info");
    }
    default: {
      const zeit = e.von ? `${startZeit(e, zone)} ` : "";
      const fuer = !creator ? zielKurz(e, n) : null;
      return pill(`${zeit}${e.titel}${fuer ? ` · ${fuer}` : ""}`, "neutral");
    }
  }
}

function zielKurz(e: Eintrag, n: Nachschlag): string | null {
  const termin = e.termin;
  if (!termin) return null;
  if (termin.audience === "all") return t("Alle", "All");
  if (termin.audience === "staff") return "Team";
  if (termin.audience === "group") return n.gruppe(termin.group_id)?.name ?? t("Gruppe", "Group");
  return talentRuf(n.talent(termin.talent_id));
}

/** Für wen gilt der Termin (Detail und Liste). */
export function zielgruppeText(e: Eintrag, p: Perspektive, n: Nachschlag, lang: Lang = currentLang()): string {
  const en = lang === "en";
  if (e.art === "holiday") return en ? "Everyone (public holiday)" : "Alle (gesetzlicher Feiertag)";
  if (istAuto(e)) return e.talentId ? (p === "creator" ? (en ? "You" : "Du") : talentVoll(n.talent(e.talentId))) : istCreatorKonto() ? t("Du", "You") : "Team";
  const termin = e.termin;
  if (!termin) return "–";
  if (termin.audience === "staff") return ta("Nur Team (intern)", "Team only (internal)", "Intern (nur für dich)", "Internal (only for you)");
  // K-527: ein Termin für mehrere Creator (Agentur: alle Namen; der Creator sieht nur seine Zeile)
  if (termin.audience === "talent" && termin.copy_group && p === "agentur") {
    const ids = n.gruppenTalente?.(termin.copy_group) ?? [];
    if (ids.length > 1) return ids.map((id) => n.talent(id)).filter((x): x is TalentLite => !!x).map(talentVoll).join(", ");
  }
  if (termin.audience === "all") return p === "creator" ? (en ? "All creators of the agency" : "Alle Creator der Agentur") : t("Alle Creator", "All creators");
  if (termin.audience === "group") {
    const g = n.gruppe(termin.group_id);
    if (!g) return p === "creator" ? (en ? "You and other creators (group)" : "Du und weitere Creator (Gruppe)") : t("Gruppe", "Group");
    const names = g.mitglieder.map((id) => n.talent(id)).filter((x): x is TalentLite => !!x).map(talentVoll);
    const hidden = g.mitglieder.length - names.length;
    if (en) return `Group ${g.name}${names.length ? `: ${names.join(", ")}` : ""}${hidden > 0 ? ` and ${hidden === 1 ? "1 more creator" : `${hidden} more creators`}` : ""}`;
    return t(`Gruppe ${g.name}${names.length ? `: ${names.join(", ")}` : ""}${hidden > 0 ? t(` und ${hidden === 1 ? t("1 weiterer Creator", "1 more creator") : t(`${hidden} weitere Creator`, `${hidden} more creators`)}`, ` and ${hidden === 1 ? t("1 weiterer Creator", "1 more creator") : t(`${hidden} weitere Creator`, `${hidden} more creators`)}`) : ""}`, `Group ${g.name}${names.length ? `: ${names.join(", ")}` : ""}${hidden > 0 ? t(` und ${hidden === 1 ? t("1 weiterer Creator", "1 more creator") : t(`${hidden} weitere Creator`, `${hidden} more creators`)}`, ` and ${hidden === 1 ? t("1 weiterer Creator", "1 more creator") : t(`${hidden} weitere Creator`, `${hidden} more creators`)}`) : ""}`);
  }
  return p === "creator" ? (en ? "You" : "Du") : talentVoll(n.talent(termin.talent_id));
}

/** Wer ist am Zug (Hinweiszeile unter dem Kalender, wie in der Vorlage „Verantwortlich: Alex“). */
export function verantwortlich(e: Eintrag, p: Perspektive, n: Nachschlag, lang: Lang = currentLang()): string | null {
  const en = lang === "en";
  const du = en ? "you" : "du";
  const agentur = en ? "your agency" : "deine Agentur";
  const creator = (id: string | null) => (p === "creator" ? du : talentRuf(n.talent(id)));
  switch (e.art) {
    case "holiday": return null;
    case "invoice_due":
      if (agenturAnCreator(e, n)) return e.talentId ? creator(e.talentId) : null;
      return n.marke(e) ? `${en ? "Brand" : "Marke"} ${n.marke(e)}` : en ? "Brand" : "Marke";
    case "payout_due": return p === "creator" ? agentur : "Team";
    case "period_requested": return e.talentId ? creator(e.talentId) : null;
    case "content_deadline": return e.talentId ? creator(e.talentId) : null;
    case "usage_rights_end": return p === "creator" ? agentur : "Team";
    case "exclusive_end": return p === "creator" ? agentur : "Team";
    case "deliverable_draft":
      if (entwurfStand(e, n) === "submitted") return p === "creator" ? agentur : "Team";
      return e.talentId ? creator(e.talentId) : null;
    case "deliverable_publish": return e.talentId ? creator(e.talentId) : null;
    default: {
      const t = e.termin;
      if (!t) return null;
      if (t.created_by_side === "talent") return p === "creator" ? du : talentRuf(n.talent(t.talent_id));
      return (p === "creator" ? null : n.mitarbeiter(t.created_by)) ?? (p === "creator" ? agentur : "Team");
    }
  }
}

/** Wer hat den Termin angelegt („Hinzugefügt von“, #57g im bisherigen Portal). */
export function hinzugefuegtVon(e: Eintrag, p: Perspektive, n: Nachschlag, lang: Lang = currentLang()): string | null {
  const t = e.termin;
  if (!t) return null;
  if (t.created_by_side === "talent") return p === "creator" ? (lang === "en" ? "You" : "Du") : `${talentVoll(n.talent(t.talent_id))} (Creator)`;
  if (p === "creator") return lang === "en" ? "Your agency" : "Deine Agentur";
  return n.mitarbeiter(t.created_by) ?? "Team";
}

/** Überschrift eines Eintrags in Liste, Hinweiszeile und Detail. */
export function eintragTitel(e: Eintrag, n: Nachschlag, lang: Lang = currentLang(), p: Perspektive = "agentur"): string {
  const en = lang === "en";
  if (e.art === "event" || e.art === "holiday") return e.titel;
  const marke = n.marke(e);
  const koop = n.kooperation(e);
  const monat = koop?.monat ? ` ${monatTitel(koop.monat, lang)}` : "";
  const nr = n.rechnung(e)?.number ?? e.titel;
  if (agenturAnCreator(e, n)) {
    return p === "creator" ? (en ? `Invoice ${nr} from your agency due` : `Rechnung ${nr} deiner Agentur fällig`)
      : t(`Rechnung ${nr} an ${e.talentId ? talentVoll(n.talent(e.talentId)) : t("den Creator", "the creator")} fällig`, `Invoice ${nr} to ${e.talentId ? talentVoll(n.talent(e.talentId)) : t("den Creator", "the creator")} due`);
  }
  if (e.art === "invoice_due") return en ? `Invoice ${nr} due${marke ? ` · ${marke}` : ""}` : `Rechnung ${nr} fällig${marke ? ` · ${marke}` : ""}`;
  // Rechnung des Creators an die Agentur: die Agentur zahlt aus (0041, Befund 15)
  if (e.art === "payout_due") {
    return en ? `Payout for invoice ${nr} due` : `Auszahlung zu Rechnung ${nr} fällig${e.talentId && p !== "creator" ? ` · ${talentVoll(n.talent(e.talentId))}` : ""}`;
  }
  if (e.art === "period_requested") return `${en ? "Invoice" : "Abrechnung"}${monat} · ${e.titel}${marke ? ` (${marke})` : ""}`;
  if (e.art === "content_deadline") return `${e.kooperationsweit ? "Deadline" : `${en ? "Content deadline" : "Content-Deadline"}${monat}`} · ${e.titel}${marke ? ` (${marke})` : ""}`;
  if (e.art === "exclusive_end") return `${en ? "End of exclusivity" : "Ende der Exklusivität"} · ${e.titel}${marke ? ` (${marke})` : ""}`;
  if (e.art === "deliverable_draft" || e.art === "deliverable_publish") {
    const was = beitragText(e, n, lang);
    const live = e.art === "deliverable_publish" && n.beitrag(e)?.status === "live";
    const stand = entwurfStand(e, n);
    const entwurf = stand === "submitted" ? (en ? "Draft submitted" : "Entwurf eingereicht") : stand === "revise" ? (en ? "Revision requested" : "Überarbeitung angefordert") : (en ? "Draft due" : "Entwurf fällig");
    const art = e.art === "deliverable_draft" ? entwurf : live ? (en ? "Published" : "Veröffentlicht") : (en ? "Publication" : "Veröffentlichung");
    return `${art}${was ? ` · ${was}` : ""} · ${e.titel}${marke ? ` (${marke})` : ""}`;
  }
  return `${en ? "End of usage rights" : "Ende der Nutzungsrechte"} · ${e.titel}${marke ? ` (${marke})` : ""}`;
}

/**
 * Hinweiszeile wie in der Vorlage: „24.09. · Content-Deadline … · Verantwortlich: Mara“. Uhrzeiten in der Zone der
 * betrachtenden Person wie im Raster (Befund 12); weicht sie von der Zone des Termins ab, steht „deine Zeit“ dabei.
 */
export function hinweisZeile(e: Eintrag, p: Perspektive, n: Nachschlag, zone: string | null = null, lang: Lang = currentLang()): string {
  const en = lang === "en";
  const wer = verantwortlich(e, p, n, lang);
  const z = e.von ? zeitText(e, zone, lang) : null;
  const zeit = z ? ` · ${z.text}${en ? "" : " Uhr"}${z.hinweis ? (en ? " (your time)" : " (deine Zeit)") : ""}` : "";
  const tag = lokaleTage(e, zone).beginn;
  return `${tagKurz(tag, lang)}${zeit} · ${eintragTitel(e, n, lang, p)}${wer ? ` · ${en ? "Responsible" : "Verantwortlich"}: ${wer.charAt(0).toUpperCase()}${wer.slice(1)}` : ""}`;
}

/** Anstehende Einträge ab heute (ohne Feiertage), höchstens n. */
export function anstehend(list: readonly Eintrag[], heute: string, bis: string, n = 6, zone?: string | null): Eintrag[] {
  return list.filter((e) => {
    const l = lokaleTage(e, zone);
    return e.art !== "holiday" && !e.ausgeblendet && (l.ende ?? l.beginn) >= heute && l.beginn <= bis;
  }).sort(sortiere).slice(0, n);
}

/** Ziel im Portal zum verknüpften Objekt (Rechnung, Kooperation, Monat). */
export function linkZiel(e: Eintrag, p: Perspektive, n: Nachschlag, lang: Lang = currentLang()): { to: string; label: string } | null {
  const en = lang === "en";
  if (e.linkKind === "invoice" && e.linkId) {
    return p === "creator" ? { to: `/creator/rechnungen?rechnung=${e.linkId}`, label: en ? "To the invoice" : "Zur Rechnung" } : { to: `/agentur/rechnungen/${e.linkId}`, label: t("Zur Rechnung", "Go to invoice") };
  }
  const koop = n.kooperation(e);
  if (!koop) return null;
  const q = koop.monat ? `?monat=${koop.monat}` : "";
  return p === "creator" ? { to: `/creator/deals/${koop.id}${q}`, label: en ? "To the deal" : "Zum Deal" } : { to: `/agentur/kooperationen/${koop.id}${q}`, label: t("Zur Kooperation", "Go to deal") };
}

/** Quelle eines automatischen Termins (TA-123). */
export function quelleText(e: Eintrag, n: Nachschlag, lang: Lang = currentLang()): string | null {
  const en = lang === "en";
  if (e.art === "holiday") return en ? "Public holiday, automatic" : "Gesetzlicher Feiertag, automatisch";
  if (!istAuto(e)) return null;
  if (e.linkKind === "invoice") return `${en ? "from invoice" : "aus Rechnung"} ${n.rechnung(e)?.number ?? e.titel}`;
  const koop = n.kooperation(e);
  if (!koop) return en ? "automatic" : "automatisch";
  return `${en ? "from collaboration" : "aus Kooperation"} ${koop.titel}${koop.monat ? `, ${en ? "month" : "Monat"} ${monatTitel(koop.monat, lang)}` : ""}`;
}

/** Termine der anderen Seite, die seit dem letzten Besuch neu sind (TA-125, AD-192); gemeinsam mit dem Zähler am Menüpunkt (neu.ts) */
export { neuSeit } from "./neu";

/** Pille in Liste und Tagesansicht: Art und Stand des Eintrags. */
export function listenPille(e: Eintrag, heute: string, neu = false, lang: Lang = currentLang(), n?: Pick<Nachschlag, "beitrag">): Pill {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  if (e.ausgeblendet) return pill(t("Ausgeblendet", "Hidden"), "neutral");
  if (neu) return pill(t("Neu", "New"), "info");
  switch (e.art) {
    case "holiday": return pill(t("Feiertag", "Holiday"), "neutral");
    case "invoice_due": return ueberfaellig(e, heute) ? pill(t("Überfällig", "Overdue"), "error") : pill(t("Zahlung fällig", "Payment due"), "warning");
    case "payout_due": return ueberfaellig(e, heute) ? pill(t("Überfällig", "Overdue"), "error") : pill(t("Auszahlung fällig", "Payout due"), "warning");
    case "content_deadline": return ueberfaellig(e, heute) ? pill(t("Überfällig", "Overdue"), "error") : pill(e.kooperationsweit ? "Deadline" : t("Content fällig", "Content due"), "warning");
    case "period_requested": return pill(t("Rechnung angefordert", "Invoice requested"), "info");
    case "usage_rights_end": return pill(t("Nutzungsrechte enden", "Usage rights end"), "neutral");
    case "exclusive_end": return pill(t("Exklusivität endet", "Exclusivity ends"), "neutral");
    case "deliverable_draft": {
      const stand = entwurfStand(e, n);
      if (stand === "submitted") return pill(t("Entwurf eingereicht · zur Prüfung", "Draft submitted · for review"), "info");
      if (ueberfaellig(e, heute, n)) return pill(t("Überfällig", "Overdue"), "error");
      return stand === "revise" ? pill(t("Überarbeitung angefordert", "Revision requested"), "warning") : pill(t("Entwurf fällig", "Draft due"), "warning");
    }
    case "deliverable_publish":
      return n?.beitrag(e)?.status === "live" ? pill("Live", "success")
        : ueberfaellig(e, heute, n) ? pill(t("Überfällig", "Overdue"), "error") : pill(t("Veröffentlichung", "Publication"), "info");
    default: return pill(t("Termin", "Date"), "neutral");
  }
}

/** Rückmeldung eines Creators zu einem Termin (0056): zugesagt, abgesagt, Rückfrage. */
export type Antwort = "accepted" | "declined" | "question";
export interface AntwortRow {
  event_id: string;
  talent_id: string;
  response: Antwort;
  note: string | null;
  responded_at: string;
}
export function antwortText(a: Antwort | null | undefined, lang: Lang = currentLang()): string {
  const en = lang === "en";
  if (a === "accepted") return en ? "Accepted" : "Zugesagt";
  if (a === "declined") return en ? "Declined" : "Abgesagt";
  if (a === "question") return en ? "Question" : "Rückfrage";
  return en ? "No answer yet" : "Noch keine Antwort";
}
export function antwortPille(a: Antwort | null | undefined, lang: Lang = currentLang()): Pill {
  return pill(antwortText(a, lang), a === "accepted" ? "success" : a === "declined" ? "error" : a === "question" ? "warning" : "neutral");
}

// ── Benachrichtigen (Bericht der Datenbank, 0041) ──────────────────────────

export interface Benachrichtigung {
  /** Creator mit Meldung in der Glocke */
  talents: number;
  /** eingereihte Mails */
  mails: number;
  /** Creator ohne hinterlegte Adresse (Anzeigename) */
  no_address: string[];
  /** Creator, die Info-Mails abbestellt haben */
  opted_out: number;
  perMail: boolean;
  /** Erinnerung nur an offene: so viele haben schon geantwortet (0056) */
  already_answered?: number;
}

/** Ehrliche Rückmeldung nach dem Benachrichtigen: Glocke, tatsächlich eingereihte Mails, fehlende Adressen, Abbestellte. */
export function berichtText(b: Benachrichtigung): { text: string; warnung: string | null } {
  if (b.talents === 0) return { text: t("Kein aktiver Creator betroffen, es ging keine Meldung raus.", "No active creator affected, no notification was sent."), warnung: null };
  const glocke = t(`${b.talents === 1 ? t("1 Creator", "1 creator") : t(`${b.talents} Creator`, `${b.talents} creators`)} in der Glocke benachrichtigt`, `${b.talents === 1 ? t("1 Creator", "1 creator") : t(`${b.talents} Creator`, `${b.talents} creators`)} notified in the notifications`);
  if (!b.perMail) return { text: `${glocke}.`, warnung: null };
  const mails = b.mails === 1 ? t("1 E-Mail eingereiht", "1 email queued") : t(`${b.mails} E-Mails eingereiht`, `${b.mails} emails queued`);
  if (b.already_answered) {
    const vorab = t(`${b.already_answered === 1 ? t("1 Creator hat", "1 creator has") : t(`${b.already_answered} Creator haben`, `${b.already_answered} creators have`)} schon geantwortet und bekommt keine Erinnerung.`, `${b.already_answered === 1 ? t("1 Creator hat", "1 creator has") : t(`${b.already_answered} Creator haben`, `${b.already_answered} creators have`)} already replied and will get no reminder.`);
    return { text: `${glocke}${b.perMail ? `, ${mails}` : ""}. ${vorab}`, warnung: null };
  }
  const teile: string[] = [];
  if (b.no_address.length) teile.push(t(`Ohne E-Mail-Adresse: ${b.no_address.join(", ")}.`, `Without email address: ${b.no_address.join(", ")}.`));
  if (b.opted_out) teile.push(t(`${b.opted_out === 1 ? t("1 Creator hat", "1 creator has") : t(`${b.opted_out} Creator haben`, `${b.opted_out} creators have`)} Info-Mails abbestellt.`, `${b.opted_out === 1 ? t("1 Creator hat", "1 creator has") : t(`${b.opted_out} Creator haben`, `${b.opted_out} creators have`)} unsubscribed from info emails.`));
  return { text: `${glocke}, ${mails}.`, warnung: teile.length ? teile.join(" ") : null };
}

/** Wie viele Creator erreicht ein Termin für alle bzw. eine Gruppe (für die Rückfrage vor einer Massenmail)? */
export function empfaengerZahl(f: Pick<TerminFormular, "zielgruppe" | "gruppeId" | "talentId"> & { talentIds?: string[] }, talents: readonly TalentLite[], mitglieder: readonly MitgliedRow[]): number {
  const aktiv = new Set(talents.filter((t) => t.status === "active").map((t) => t.id));
  if (f.zielgruppe === "all") return aktiv.size;
  if (f.zielgruppe === "group") return mitglieder.filter((m) => m.group_id === f.gruppeId && aktiv.has(m.talent_id)).length;
  if (f.zielgruppe === "talent") return f.talentIds?.length ? f.talentIds.length : f.talentId ? 1 : 0;
  return 0;
}

// ── Formulare ──────────────────────────────────────────────────────────────

export interface TerminFormular {
  titel: string;
  beschreibung: string;
  datum: string;
  bisDatum: string;
  von: string;
  bis: string;
  zeitzone: string;
  farbe: Farbe;
  zielgruppe: Zielgruppe;
  gruppeId: string;
  talentId: string;
  /** Ort (0041), geht mit in die Mail */
  ort: string;
  /** 0395 (K-527): Link für Video-Calls */
  link: string;
  /** 0395 (K-527): Agentur: ein oder mehrere Creator (Zielgruppe „talent“) */
  talentIds: string[];
  /** Agentur: Creator in der Glocke benachrichtigen (calendar_event_notify) */
  benachrichtigen: boolean;
  perMail: boolean;
}

export interface TerminDaten {
  title: string;
  description: string | null;
  starts_on: string;
  ends_on: string | null;
  start_time: string | null;
  end_time: string | null;
  time_zone: string;
  location: string | null;
  meeting_url: string | null;
  color: Farbe;
  audience: Zielgruppe;
  group_id: string | null;
  talent_id: string | null;
}

export function leeresFormular(datum: string, zeitzone: string, p: Perspektive, eigeneTalentId: string | null = null): TerminFormular {
  return {
    titel: "", beschreibung: "", datum, bisDatum: "", von: "", bis: "", zeitzone, farbe: "blue",
    zielgruppe: p === "creator" ? "talent" : "staff", gruppeId: "", talentId: p === "creator" ? eigeneTalentId ?? "" : "", ort: "", link: "",
    talentIds: p === "creator" && eigeneTalentId ? [eigeneTalentId] : [],
    benachrichtigen: p === "agentur", perMail: p === "agentur",
  };
}

/** Formular aus einem Termin; geschwister: alle Zeilen derselben Gruppe (K-527, mehrere Creator) */
export function formularAusTermin(t: TerminRow, geschwister: readonly TerminRow[] = []): TerminFormular {
  const ids = t.copy_group ? [...new Set([t, ...geschwister].filter((x) => x.copy_group === t.copy_group && x.talent_id).map((x) => x.talent_id!))] : t.talent_id ? [t.talent_id] : [];
  return {
    titel: t.title, beschreibung: t.description ?? "", datum: t.starts_on, bisDatum: t.ends_on ?? "", von: hm(t.start_time) ?? "", bis: hm(t.end_time) ?? "",
    zeitzone: t.time_zone, farbe: farbe(t.color), zielgruppe: t.audience, gruppeId: t.group_id ?? "", talentId: t.talent_id ?? "", ort: t.location ?? "",
    link: t.meeting_url ?? "", talentIds: ids,
    // Beim Bearbeiten bewusst aus: sonst meldet jede Korrektur (Tippfehler, Farbe) erneut an alle (bisheriges Portal)
    benachrichtigen: false, perMail: false,
  };
}

const ZEIT = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Prüft das Formular wie die Datenbank (Längen, Datumsfolge, Zielgruppe) und liefert die Zeile. */
export function pruefeTermin(f: TerminFormular, p: Perspektive, lang: Lang = currentLang()): { fehler: Record<string, string>; daten: TerminDaten | null; talentIds: string[] } {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const fehler: Record<string, string> = {};
  const titel = f.titel.trim();
  if (!titel) fehler.titel = t("Bitte einen Titel eingeben.", "Please enter a title.");
  else if (titel.length > 200) fehler.titel = t("Der Titel darf höchstens 200 Zeichen haben.", "The title may have at most 200 characters.");
  if (f.beschreibung.length > 2000) fehler.beschreibung = t("Die Beschreibung darf höchstens 2.000 Zeichen haben.", "The description may have at most 2,000 characters.");
  if ((f.ort ?? "").trim().length > 200) fehler.ort = t("Der Ort darf höchstens 200 Zeichen haben.", "The location may have at most 200 characters.");
  const link = (f.link ?? "").trim();
  if (link && (!/^https:\/\/[^\s"<>\\]+$/.test(link) || link.length > 500)) fehler.link = t("Der Link muss mit https:// beginnen.", "The link must start with https://.");
  if (!isIsoDate(f.datum)) fehler.datum = t("Bitte ein gültiges Datum wählen.", "Please choose a valid date.");
  if (f.bisDatum && !isIsoDate(f.bisDatum)) fehler.bisDatum = t("Bitte ein gültiges Enddatum wählen.", "Please choose a valid end date.");
  else if (f.bisDatum && isIsoDate(f.datum) && f.bisDatum < f.datum) fehler.bisDatum = t("Das Enddatum darf nicht vor dem Startdatum liegen.", "The end date cannot be before the start date.");
  if (f.von && !ZEIT.test(f.von)) fehler.von = t("Bitte eine Uhrzeit wie 10:00 eingeben.", "Please enter a time like 10:00.");
  if (f.bis && !ZEIT.test(f.bis)) fehler.bis = t("Bitte eine Uhrzeit wie 11:00 eingeben.", "Please enter a time like 11:00.");
  if (f.bis && !f.von) fehler.bis = t("Bitte zuerst den Beginn eintragen.", "Please enter the start first.");
  if (f.von && f.bis && ZEIT.test(f.von) && ZEIT.test(f.bis) && (!f.bisDatum || f.bisDatum === f.datum) && f.bis <= f.von) {
    fehler.bis = t("Das Ende muss nach dem Beginn liegen.", "The end must be after the start.");
  }
  if (!f.zeitzone) fehler.zeitzone = t("Bitte eine Zeitzone wählen.", "Please choose a time zone.");
  const zielgruppe: Zielgruppe = p === "creator" ? "talent" : f.zielgruppe;
  if (zielgruppe === "group" && !f.gruppeId) fehler.gruppeId = t("Bitte eine Gruppe wählen.", "Please choose a group.");
  // K-527: die Agentur wählt einen oder mehrere Creator (talentIds), der Creator trägt für sich selbst ein (talentId)
  const ids = p === "creator" ? (f.talentId ? [f.talentId] : []) : (f.talentIds?.length ? f.talentIds : f.talentId ? [f.talentId] : []);
  if (zielgruppe === "talent" && !ids.length) fehler.talentId = t("Bitte mindestens einen Creator wählen.", "Please choose at least one creator.");
  if (Object.keys(fehler).length) return { fehler, daten: null, talentIds: [] };
  return {
    fehler,
    talentIds: zielgruppe === "talent" ? ids : [],
    daten: {
      title: titel, description: f.beschreibung.trim() || null, starts_on: f.datum,
      ends_on: f.bisDatum && f.bisDatum !== f.datum ? f.bisDatum : null,
      start_time: f.von || null, end_time: f.von ? f.bis || null : null, time_zone: f.zeitzone, location: (f.ort ?? "").trim() || null, meeting_url: link || null,
      color: f.farbe, audience: zielgruppe,
      group_id: zielgruppe === "group" ? f.gruppeId : null, talent_id: zielgruppe === "talent" ? ids[0]! : null,
    },
  };
}

export interface UeberschreibFormular {
  titel: string;
  datum: string;
  farbe: Farbe;
}

export function pruefeUeberschreibung(f: UeberschreibFormular): { fehler: Record<string, string>; daten: { title: string; starts_on: string; color: Farbe } | null } {
  const fehler: Record<string, string> = {};
  const titel = f.titel.trim();
  if (!titel) fehler.titel = t("Bitte einen Titel eingeben.", "Please enter a title.");
  else if (titel.length > 200) fehler.titel = t("Der Titel darf höchstens 200 Zeichen haben.", "The title may have at most 200 characters.");
  if (!isIsoDate(f.datum)) fehler.datum = t("Bitte ein gültiges Datum wählen.", "Please choose a valid date.");
  return Object.keys(fehler).length ? { fehler, daten: null } : { fehler, daten: { title: titel, starts_on: f.datum, color: f.farbe } };
}

/** Zeile für eine neue Überschreibung eines automatischen Termins (source_key = Feed-Kennung, AD-190). */
export function ueberschreibungsZeile(e: Eintrag, orgId: string, zone: string, daten: { title: string; starts_on: string; color: Farbe; hidden?: boolean }) {
  return {
    org_id: orgId, source_key: e.key, title: daten.title, starts_on: daten.starts_on, color: daten.color, hidden: daten.hidden ?? false,
    time_zone: e.zeitzone ?? zone,
    // Am Talent festmachen: eingeschränkte Manager sehen und ändern nur Überschreibungen ihrer Creator (RLS)
    audience: e.talentId ? "talent" : "staff", talent_id: e.talentId,
  };
}

/**
 * Zeitzonen zur Auswahl: die des Termins, der betrachtenden Person, der Agentur und übliche Drehorte zuerst, danach alle
 * Zonen, die der Browser kennt (Befund 15: auch Bali, Thailand, Australien).
 */
export function zeitzonen(...eigene: (string | null | undefined)[]): string[] {
  const z = zonenAuswahl(...eigene);
  return [...z.haeufig, ...z.alle];
}

export function browserZone(fallback: string): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || fallback;
  } catch {
    return fallback;
  }
}

/** Gruppe prüfen (Name 1–80 Zeichen wie die Datenbank). */
export function pruefeGruppe(name: string, mitglieder: readonly string[]): { fehler: Record<string, string>; name: string } {
  const n = name.trim();
  const fehler: Record<string, string> = {};
  if (!n) fehler.name = t("Bitte einen Namen eingeben.", "Please enter a name.");
  else if (n.length > 80) fehler.name = t("Der Name darf höchstens 80 Zeichen haben.", "The name may have at most 80 characters.");
  if (!mitglieder.length) fehler.mitglieder = t("Bitte mindestens einen Creator wählen.", "Please choose at least one creator.");
  return { fehler, name: n };
}

/**
 * Bereich der Feed-Abfrage für einen Monat: zwei Monate davor und danach. Der Feed filtert automatische Termine nach ihrem
 * ursprünglichen Tag; ein verschobener Termin (Überschreibung) kommt nur mit, wenn sein ursprünglicher Tag im Bereich liegt.
 */
export function abfrageBereich(month: string): { von: string; bis: string } {
  return { von: `${addMonths(month, -2)}-01`, bis: lastDayOfMonth(addMonths(month, 2)) };
}

/** Jahre, deren Feiertage in den Abfragebereich fallen. */
export function jahreFuer(month: string): number[] {
  const { von, bis } = abfrageBereich(month);
  const out: number[] = [];
  for (let y = Number(von.slice(0, 4)); y <= Number(bis.slice(0, 4)); y++) out.push(y);
  return out;
}

/** Einträge, die den Monat berühren (für Raster und Liste), Termine mit Uhrzeit am Tag der betrachtenden Person. */
export function imMonat(list: readonly Eintrag[], month: string, zone?: string | null): Eintrag[] {
  const von = `${month}-01`;
  const bis = lastDayOfMonth(month);
  return list.filter((e) => {
    const l = lokaleTage(e, zone);
    return l.beginn <= bis && (l.ende ?? l.beginn) >= von;
  }).sort(sortiere);
}
